/**
 * Consultant Reports Page
 *
 * Reports page where consultants can view analytics, generate reports,
 * and track their performance metrics.
 *
 * Features:
 * - Report type selector (Pre-Audit Summary, CAR Report, Client Progress)
 * - Date range filter
 * - Client filter
 * - Report preview area
 * - Export buttons (PDF, Excel)
 * - Performance metrics section
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, BarChart3, Users, ClipboardList } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { formatDate } from '../../utils/formatting';

type ReportType = 'PRE_AUDIT_SUMMARY' | 'CAR_REPORT' | 'CLIENT_PROGRESS' | 'MONTHLY_ACTIVITY';

interface ReportFilter {
  reportType: ReportType;
  startDate: string;
  endDate: string;
  selectedClients: string[];
}

// Mock data for demonstration
const MOCK_CLIENTS = [
  { id: 'app-001', name: 'Restoran Al-Barakah Sdn Bhd' },
  { id: 'app-002', name: 'Kembara Food Industries' },
  { id: 'app-003', name: 'Nusantara Bakery & Confectionery' },
];

const MOCK_PERFORMANCE_METRICS = {
  totalClients: 12,
  activePreAudits: 4,
  completedThisMonth: 8,
  carsIssued: 15,
  averageReadinessScore: 78,
  onTimeCompletionRate: 92,
};

export const ConsultantReportsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const navigate = useNavigate();

  // Calculate default date range
  const getDefaultDateRange = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    };
  };

  const [filters, setFilters] = useState<ReportFilter>({
    reportType: 'PRE_AUDIT_SUMMARY',
    ...getDefaultDateRange(),
    selectedClients: [],
  });

  const [showPreview, setShowPreview] = useState(false);

  const handleFilterChange = (field: keyof ReportFilter, value: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setShowPreview(false);
  };

  const handleClientToggle = (clientId: string) => {
    setFilters((prev) => ({
      ...prev,
      selectedClients: prev.selectedClients.includes(clientId)
        ? prev.selectedClients.filter((id) => id !== clientId)
        : [...prev.selectedClients, clientId],
    }));
    setShowPreview(false);
  };

  const handleGeneratePreview = () => {
    setShowPreview(true);
  };

  const handleExportPDF = () => {
    alert('Exporting report as PDF...');
  };

  const handleExportExcel = () => {
    alert('Exporting report as Excel...');
  };

  const getReportTypeLabel = (type: ReportType): string => {
    switch (type) {
      case 'PRE_AUDIT_SUMMARY':
        return t('consultant:reports.preAuditReport');
      case 'CAR_REPORT':
        return 'CAR Report';
      case 'CLIENT_PROGRESS':
        return t('consultant:reports.clientProgress');
      case 'MONTHLY_ACTIVITY':
        return t('consultant:reports.monthlyActivity');
    }
  };

  // Mock report preview data
  const getReportPreview = () => {
    if (!showPreview) return null;

    const clientsText = filters.selectedClients.length === 0
      ? t('consultant:reports.allClients')
      : filters.selectedClients.map((id) => MOCK_CLIENTS.find((c) => c.id === id)?.name).join(', ');

    return (
      <div className="space-y-4">
        <div className="border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-900">{getReportTypeLabel(filters.reportType)}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('consultant:reports.dateRange')}: {formatDate(filters.startDate)} - {formatDate(filters.endDate)}
          </p>
          <p className="text-sm text-gray-600">Clients: {clientsText}</p>
        </div>

        {filters.reportType === 'PRE_AUDIT_SUMMARY' && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t('consultant:reports.summary')}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded">
                <p className="text-sm text-gray-600">Total Pre-Audits</p>
                <p className="text-2xl font-bold text-blue-600">12</p>
              </div>
              <div className="p-3 bg-green-50 rounded">
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">8</p>
              </div>
              <div className="p-3 bg-orange-50 rounded">
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-orange-600">4</p>
              </div>
              <div className="p-3 bg-purple-50 rounded">
                <p className="text-sm text-gray-600">Avg Readiness</p>
                <p className="text-2xl font-bold text-purple-600">78%</p>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold mb-2">Recent Pre-Audits</h4>
              <div className="space-y-2">
                {MOCK_CLIENTS.slice(0, 3).map((client) => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">{client.name}</p>
                      <p className="text-sm text-gray-600">Completed: {formatDate(new Date().toISOString())}</p>
                    </div>
                    <Badge variant="success">Complete</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {filters.reportType === 'CAR_REPORT' && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Corrective Action Reports</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-red-50 rounded">
                <p className="text-sm text-gray-600">Major</p>
                <p className="text-2xl font-bold text-red-600">3</p>
              </div>
              <div className="p-3 bg-orange-50 rounded">
                <p className="text-sm text-gray-600">Minor</p>
                <p className="text-2xl font-bold text-orange-600">9</p>
              </div>
              <div className="p-3 bg-blue-50 rounded">
                <p className="text-sm text-gray-600">Observations</p>
                <p className="text-2xl font-bold text-blue-600">6</p>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold mb-2">Open CARs</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">CAR-{Date.now() - 100000}</p>
                    <p className="text-sm text-gray-600">Halal Policy Documentation</p>
                  </div>
                  <Badge variant="error">Major</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">CAR-{Date.now() - 200000}</p>
                    <p className="text-sm text-gray-600">Storage Segregation</p>
                  </div>
                  <Badge variant="warning">Minor</Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {filters.reportType === 'CLIENT_PROGRESS' && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Client Progress Overview</h3>
            <div className="space-y-2">
              {MOCK_CLIENTS.map((client) => (
                <div key={client.id} className="p-4 bg-gray-50 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{client.name}</h4>
                    <Badge variant="success">On Track</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Readiness</p>
                      <p className="font-semibold">85%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Pre-Audit</p>
                      <p className="font-semibold">Complete</p>
                    </div>
                    <div>
                      <p className="text-gray-600">CARs</p>
                      <p className="font-semibold">2 Open</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {filters.reportType === 'MONTHLY_ACTIVITY' && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Monthly Activity Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded">
                <p className="text-sm text-gray-600">Pre-Audits Conducted</p>
                <p className="text-2xl font-bold text-blue-600">8</p>
              </div>
              <div className="p-3 bg-green-50 rounded">
                <p className="text-sm text-gray-600">CARs Issued</p>
                <p className="text-2xl font-bold text-green-600">15</p>
              </div>
              <div className="p-3 bg-purple-50 rounded">
                <p className="text-sm text-gray-600">Clients Served</p>
                <p className="text-2xl font-bold text-purple-600">12</p>
              </div>
              <div className="p-3 bg-orange-50 rounded">
                <p className="text-sm text-gray-600">Hours Logged</p>
                <p className="text-2xl font-bold text-orange-600">96</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout
      pageTitle={t('consultant:reports.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
        { label: t('consultant:reports.title') },
      ]}
      actions={
        showPreview ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} icon={<FileText className="w-4 h-4" />}>
              {t('consultant:reports.exportPDF')}
            </Button>
            <Button variant="outline" onClick={handleExportExcel} icon={<BarChart3 className="w-4 h-4" />}>
              {t('consultant:reports.exportExcel')}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Report Filters & Preview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Report Filters</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {/* Report Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('consultant:reports.reportType')}
                  </label>
                  <select
                    value={filters.reportType}
                    onChange={(e) => handleFilterChange('reportType', e.target.value as ReportType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PRE_AUDIT_SUMMARY">{t('consultant:reports.preAuditReport')}</option>
                    <option value="CAR_REPORT">CAR Report</option>
                    <option value="CLIENT_PROGRESS">{t('consultant:reports.clientProgress')}</option>
                    <option value="MONTHLY_ACTIVITY">{t('consultant:reports.monthlyActivity')}</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Client Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('consultant:reports.selectClient')} (Optional)
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {MOCK_CLIENTS.map((client) => (
                      <label key={client.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filters.selectedClients.includes(client.id)}
                          onChange={() => handleClientToggle(client.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{client.name}</span>
                      </label>
                    ))}
                  </div>
                  {filters.selectedClients.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {filters.selectedClients.length} client(s) selected
                    </p>
                  )}
                </div>

                {/* Generate Button */}
                <Button fullWidth onClick={handleGeneratePreview} icon={<BarChart3 className="w-4 h-4" />}>
                  {t('consultant:reports.generateReport')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Report Preview */}
          {showPreview ? (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Report Preview</h3>
              </CardHeader>
              <CardBody>{getReportPreview()}</CardBody>
            </Card>
          ) : (
            <Card>
              <CardBody>
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 text-neutral-400" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {t('consultant:reports.noReports')}
                  </h3>
                  <p className="text-gray-600">
                    Select filters and click "Generate Report" to view the preview
                  </p>
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar - Performance Metrics */}
        <div className="space-y-6">
          {/* Performance Metrics */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Performance Metrics</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                  <span className="text-sm text-gray-700">Total Clients</span>
                  <span className="text-xl font-bold text-blue-600">{MOCK_PERFORMANCE_METRICS.totalClients}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                  <span className="text-sm text-gray-700">Active Pre-Audits</span>
                  <span className="text-xl font-bold text-orange-600">{MOCK_PERFORMANCE_METRICS.activePreAudits}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                  <span className="text-sm text-gray-700">Completed This Month</span>
                  <span className="text-xl font-bold text-green-600">{MOCK_PERFORMANCE_METRICS.completedThisMonth}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                  <span className="text-sm text-gray-700">CARs Issued</span>
                  <span className="text-xl font-bold text-purple-600">{MOCK_PERFORMANCE_METRICS.carsIssued}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded">
                  <span className="text-sm text-gray-700">Avg Readiness Score</span>
                  <span className="text-xl font-bold text-indigo-600">{MOCK_PERFORMANCE_METRICS.averageReadinessScore}%</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-teal-50 rounded">
                  <span className="text-sm text-gray-700">On-Time Rate</span>
                  <span className="text-xl font-bold text-teal-600">{MOCK_PERFORMANCE_METRICS.onTimeCompletionRate}%</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Quick Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button fullWidth variant="outline" onClick={() => navigate('/consultant/clients')} icon={<Users className="w-4 h-4" />}>
                  {t('consultant:clients.title')}
                </Button>
                <Button fullWidth variant="outline" onClick={() => navigate('/consultant/dashboard')} icon={<BarChart3 className="w-4 h-4" />}>
                  {t('consultant:dashboard.title')}
                </Button>
                <Button fullWidth variant="outline" onClick={() => navigate('/consultant/invoices')} icon={<FileText className="w-4 h-4" />}>
                  Invoices
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Report Types Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-neutral-600" />
                <h3 className="text-lg font-semibold">Report Types</h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-gray-900">Pre-Audit Summary</p>
                  <p className="text-gray-600">Overview of pre-audit activities and readiness scores</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">CAR Report</p>
                  <p className="text-gray-600">Summary of corrective action reports issued</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Client Progress</p>
                  <p className="text-gray-600">Track client progress towards certification</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Monthly Activity</p>
                  <p className="text-gray-600">Your activity summary for the selected period</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default ConsultantReportsPage;

